/** source_uri の末尾セグメントを一覧向けタイトル候補にする（BFF rag-format と同趣旨）。 */
export function titleFromSourceUri(sourceUri: string): string {
  const uri = String(sourceUri ?? '').trim()
  if (!uri) {
    return ''
  }
  const parts = uri.split('/').filter(Boolean)
  return parts[parts.length - 1] ?? uri
}

/** 一覧表示用タイトル。UUID っぽいだけのときは URI 末尾を優先。 */
export function displayDocumentTitle(
  title: string | undefined,
  sourceUri: string,
  documentId?: string,
): string {
  const trimmed = String(title ?? '').trim()
  const fromUri = titleFromSourceUri(sourceUri)
  if (!trimmed || trimmed === documentId || looksLikeUuid(trimmed)) {
    return fromUri || trimmed || documentId || '（無題）'
  }
  return trimmed
}

const OFFICE_PREFIX = 'office://'

const SOURCE_TYPE_LABELS: Record<string, string> = {
  workflow: 'ワークフロー',
  schedule: 'スケジュール',
  bulletin: '掲示板',
  project: 'プロジェクト',
  report: 'レポート',
}

/** source_uri の種別（office:// の直後）を人間向けラベルにする。 */
export function sourceTypeLabel(sourceUri: string): string {
  const parsed = parseOfficeSourceUri(sourceUri)
  if (!parsed) {
    return '出典'
  }
  return SOURCE_TYPE_LABELS[parsed.sourceType] ?? parsed.sourceType
}

/**
 * source_uri → 出典ラベル（設計書 §6.2）。
 * 例: office://workflow/form1/request/12345 → 「ワークフロー — 申請 #12345」
 */
export function sourceUriLabel(sourceUri: string): string {
  const parsed = parseOfficeSourceUri(sourceUri)
  if (!parsed) {
    const trimmed = String(sourceUri ?? '').trim()
    return trimmed || '出典不明'
  }

  const typeLabel = SOURCE_TYPE_LABELS[parsed.sourceType] ?? parsed.sourceType
  const { segments } = parsed

  if (parsed.sourceType === 'workflow' && segments[1] === 'request' && segments[2]) {
    return `${typeLabel} — 申請 #${segments[2]}`
  }
  if (parsed.sourceType === 'schedule' && segments[1] === 'event' && segments[2]) {
    return `${typeLabel} — 予定 #${segments[2]}`
  }
  if (segments[1] === 'record' && segments[2]) {
    return `${typeLabel} — ${segments[2]}`
  }

  const tail = segments[segments.length - 1]
  return tail ? `${typeLabel} — ${tail}` : typeLabel
}

/** body / snippet 内の `参照URL:` 行から http(s) URL を取り出す（無ければ null）。 */
export function extractReferenceUrl(text: string): string | null {
  for (const line of String(text ?? '').split('\n')) {
    const trimmed = line.trim()
    if (!trimmed.startsWith('参照URL:')) {
      continue
    }
    const value = trimmed.slice('参照URL:'.length).trim()
    if (/^https?:\/\//i.test(value)) {
      return value
    }
  }
  return null
}

type ParsedOfficeUri = {
  sourceType: string
  /** office://{sourceType}/ 以降を `/` 分割 */
  segments: string[]
}

function parseOfficeSourceUri(sourceUri: string): ParsedOfficeUri | null {
  const uri = String(sourceUri ?? '').trim()
  if (!uri.startsWith(OFFICE_PREFIX)) {
    return null
  }
  const rest = uri.slice(OFFICE_PREFIX.length)
  const segments = rest.split('/').filter(Boolean)
  if (segments.length === 0) {
    return null
  }
  return { sourceType: segments[0], segments }
}

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value,
  )
}
